import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Server,
  Globe,
  Database,
  ArrowRight,
  Sparkles,
  BarChart3,
  CheckCircle2,
  Clock,
  FileText,
  Lightbulb,
  Target,
  Layers,
} from 'lucide-react';
import { api } from '../lib/api';
import { SESSION_CACHE } from '../lib/queryCache';
import Button from '../components/ui/Button';
import Card, { CardBody } from '../components/ui/Card';
import Spinner from '../components/ui/Spinner';

// Methodology phase data
const methodologyPhases = [
  {
    id: 'discovery',
    title: 'DISCOVERY',
    duration: '<1 WEEK',
    color: 'from-purple-900 to-purple-700',
    borderColor: 'border-purple-500',
    items: [
      'Industry Research Database',
      'Company Public Data Research',
      'Peer Company Public Data Research',
      'Custom/Proprietary Data Ingestion',
      'Knowledge Graph & Vector DB Build',
    ],
  },
  {
    id: 'analysis',
    title: 'ANALYSIS',
    duration: '<1 WEEK',
    color: 'from-purple-700 to-purple-500',
    borderColor: 'border-purple-400',
    items: [
      'Financials and Ratio Analysis',
      'Enterprise Value Added Analysis',
      'Industry Value Chain Analysis',
      'CXO Persona Analysis',
      'Hypothesis Generation',
    ],
  },
  {
    id: 'design',
    title: 'DESIGN',
    duration: '1-2 WEEKS',
    color: 'from-purple-500 to-purple-300',
    borderColor: 'border-purple-300',
    items: [
      'Hypothesis Finalization',
      'Recommendation Engine',
      'Opportunity and Impact Sizing',
      'Conversational Interface',
      'Technology Integration Blueprint',
    ],
  },
];

export default function Dashboard() {
  const navigate = useNavigate();

  // Fetch companies count - API returns array directly
  const { data: companiesData, isLoading: loadingCompanies } = useQuery({
    queryKey: ['companies-count'],
    queryFn: async () => {
      const response = await api.get<any[]>('/companies');
      return response;
    },
    ...SESSION_CACHE,
  });

  // Fetch domain packs count - API returns array directly
  const { data: domainPacksData, isLoading: loadingDomainPacks } = useQuery({
    queryKey: ['domain-packs-count'],
    queryFn: async () => {
      const response = await api.get<any[]>('/domain-packs');
      return response;
    },
    ...SESSION_CACHE,
  });

  // Fetch industries count - API returns array directly
  const { data: industriesData, isLoading: loadingIndustries } = useQuery({
    queryKey: ['industries-count'],
    queryFn: async () => {
      const response = await api.get<any[]>('/industries');
      return response;
    },
    ...SESSION_CACHE,
  });

  // Fetch enterprise systems count - API returns object with systems array
  const { data: enterpriseSystemsData, isLoading: loadingEnterpriseSystems } = useQuery({
    queryKey: ['enterprise-systems-count'],
    queryFn: async () => {
      const response = await api.get<{ systems: any[]; total: number }>('/enterprise-systems');
      return response;
    },
    ...SESSION_CACHE,
  });

  // Fetch cases count - API returns object with cases array
  const { data: casesData, isLoading: loadingCases } = useQuery({
    queryKey: ['cases-count'],
    queryFn: async () => {
      const response = await api.get<{ cases: any[]; total?: number }>('/cases');
      return response;
    },
    ...SESSION_CACHE,
  });

  const isLoading = loadingCompanies || loadingDomainPacks || loadingIndustries || loadingEnterpriseSystems || loadingCases;

  // Compute secondary stats from fetched data
  const companiesCount = Array.isArray(companiesData) ? companiesData.length : 0;
  const totalReports = Array.isArray(companiesData)
    ? companiesData.reduce((sum, c) => sum + (c.reportCount || 0), 0)
    : 0;

  const domainPacksCount = Array.isArray(domainPacksData) ? domainPacksData.length : 0;
  const totalSubdomains = Array.isArray(domainPacksData)
    ? domainPacksData.reduce((sum, d) => sum + (d.subdomain_count || 0), 0)
    : 0;

  const industriesCount = Array.isArray(industriesData) ? industriesData.length : 0;
  const totalVersions = Array.isArray(industriesData)
    ? industriesData.reduce((sum, i) => sum + (i.totalVersions || 0), 0)
    : 0;

  // Get cases count from cases API
  const casesCount = casesData?.cases?.length || 0;
  const activeCasesCount = casesData?.cases?.filter((c: any) => c.active)?.length || 0;

  const enterpriseSystemsCount = enterpriseSystemsData?.total || enterpriseSystemsData?.systems?.length || 0;
  const totalModules = Array.isArray(enterpriseSystemsData?.systems)
    ? enterpriseSystemsData.systems.reduce((sum, s) => sum + (s.total_modules || 0), 0)
    : 0;
  const totalTables = Array.isArray(enterpriseSystemsData?.systems)
    ? enterpriseSystemsData.systems.reduce((sum, s) => sum + (s.total_tables || 0), 0)
    : 0;

  const stats = [
    {
      label: 'Companies Researched',
      value: companiesCount,
      icon: Building2,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      href: '/companies',
      secondaryStats: [
        { label: 'Financial Reports', value: totalReports },
      ],
    },
    {
      label: 'Domain Packs',
      value: domainPacksCount,
      icon: Layers,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      href: '/domain-packs',
      secondaryStats: [
        { label: 'Subdomains', value: totalSubdomains },
      ],
    },
    {
      label: 'Industries Analyzed',
      value: industriesCount,
      icon: Globe,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      href: '/industries',
      secondaryStats: [
        { label: 'Active Cases', value: activeCasesCount },
        { label: 'Total Cases', value: casesCount },
      ],
    },
    {
      label: 'Enterprise Systems',
      value: enterpriseSystemsCount,
      icon: Server,
      color: 'bg-orange-500',
      bgColor: 'bg-orange-50',
      href: '/enterprise-systems',
      secondaryStats: [
        { label: 'Modules', value: totalModules },
        { label: 'Tables', value: totalTables },
      ],
    },
  ];

  return (
    <div className="max-w-screen-2xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-[40px] font-bold text-neutral-900 mb-2">
          Business Transformation Studio
        </h1>
        <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
          Accelerate your digital transformation journey with AI-powered insights,
          knowledge graph design, and strategic consulting methodology.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <button
            key={stat.label}
            onClick={() => navigate(stat.href)}
            className="text-left hover:shadow-lg transition-all group"
          >
            <Card className="h-full">
              <CardBody className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`w-12 h-12 ${stat.bgColor} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0`}>
                    <stat.icon className={`w-6 h-6 ${stat.color.replace('bg-', 'text-')}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-neutral-500 mb-0.5">{stat.label}</p>
                    {isLoading ? (
                      <Spinner size="sm" />
                    ) : (
                      <p className="text-2xl font-bold text-neutral-900">{stat.value}</p>
                    )}
                  </div>
                  <ArrowRight className="w-5 h-5 text-neutral-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                </div>
                {/* Secondary Stats */}
                {!isLoading && stat.secondaryStats && stat.secondaryStats.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-neutral-100">
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {stat.secondaryStats.map((secondary) => (
                        <div key={secondary.label} className="flex items-center gap-1.5 text-xs text-neutral-500">
                          <span className="font-medium text-neutral-700">{secondary.value}</span>
                          <span>{secondary.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </button>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200">
          <CardBody className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-neutral-900 mb-2">Start Discovery</h3>
                <p className="text-sm text-neutral-600 mb-4">
                  Begin a new case with AI-powered company and industry research.
                </p>
                <Button size="sm" onClick={() => navigate('/cases/new')}>
                  New Case
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardBody className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-neutral-900 mb-2">View Cases</h3>
                <p className="text-sm text-neutral-600 mb-4">
                  Manage your existing cases across Discovery, Analysis, and Design phases.
                </p>
                <Button size="sm" variant="secondary" onClick={() => navigate('/case-library')}>
                  Case Library
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardBody className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-neutral-900 mb-2">AI Assistant</h3>
                <p className="text-sm text-neutral-600 mb-4">
                  Chat with AI to explore insights and get strategic recommendations.
                </p>
                <Button size="sm" variant="outline" onClick={() => navigate('/chat')}>
                  Open Chat
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Methodology Section */}
      <Card>
        <CardBody className="p-0 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-950 via-purple-900 to-purple-800 p-8">
            <h2 className="text-2xl font-bold text-white text-center mb-2">
              Our Methodology
            </h2>
            <p className="text-purple-200 text-center mb-8">
              A proven 3-phase approach to digital transformation
            </p>

            {/* Phases */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 relative">
              {/* Connecting lines for desktop */}
              <div className="hidden lg:block absolute top-12 left-1/3 w-1/3 h-0.5 bg-gradient-to-r from-purple-400 to-purple-300" />
              <div className="hidden lg:block absolute top-12 right-1/3 w-1/3 h-0.5 bg-gradient-to-r from-purple-300 to-purple-200" />

              {methodologyPhases.map((phase, index) => (
                <div key={phase.id} className="relative">
                  {/* Duration badge */}
                  <div className="flex justify-center mb-4">
                    <span className="px-4 py-1 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium text-purple-200 border border-purple-400/30">
                      {phase.duration}
                    </span>
                  </div>

                  {/* Phase card */}
                  <div className={`bg-gradient-to-b ${phase.color} rounded-2xl p-6 border ${phase.borderColor} shadow-xl`}>
                    <h3 className="text-xl font-bold text-white text-center mb-6 tracking-wider">
                      {phase.title}
                    </h3>

                    <div className="space-y-3">
                      {phase.items.map((item, itemIndex) => (
                        <div
                          key={itemIndex}
                          className="bg-white/95 backdrop-blur-sm rounded-lg px-4 py-2.5 text-sm text-purple-900 font-medium shadow-sm hover:shadow-md transition-shadow"
                        >
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Arrow for mobile */}
                  {index < methodologyPhases.length - 1 && (
                    <div className="lg:hidden flex justify-center py-4">
                      <ArrowRight className="w-8 h-8 text-purple-400 rotate-90" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <CardBody className="p-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <Database className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="font-semibold text-lg text-neutral-900 mb-2">
              Knowledge Graph Design
            </h3>
            <p className="text-sm text-neutral-600">
              Automatically generate Neo4j schemas from your enterprise systems with AI-powered entity extraction.
            </p>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="p-6">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <Lightbulb className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className="font-semibold text-lg text-neutral-900 mb-2">
              Strategic Hypotheses
            </h3>
            <p className="text-sm text-neutral-600">
              Generate MBB-style strategic hypotheses based on industry analysis and company research.
            </p>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="p-6">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <FileText className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="font-semibold text-lg text-neutral-900 mb-2">
              Integration Blueprints
            </h3>
            <p className="text-sm text-neutral-600">
              Get ready-to-deploy integration patterns and code generation for your technology stack.
            </p>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
